"use client";

import { c as _c } from "react/compiler-runtime";
import InsuranceEditDialog from "../../dialogs/insurance-edit-dialog";
import { useQuery } from "@tanstack/react-query";
import { FiEdit } from "react-icons/fi";
import LabeledValue from "@/components/labeled-value";
import { Button } from "@/components/ui";
import CollapsibleSection from "@/components/ui/collapsible-section";
import { formatDate, formatPhoneNumber } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import type { InsuranceCoverage } from "@/types/insurance-coverage";
import type { PatientDetail } from "@/types/patient";
import { insuranceQueryOptions } from "@/api/provider/user/patient/insurance-query";
interface InsuranceSectionProps {
  patient: PatientDetail;
  shouldUseTwoColumns: boolean;
}
export default function InsuranceSection(t0) {
  const $ = _c(9);
  const {
    patient,
    shouldUseTwoColumns
  } = t0;
  let t1;
  if ($[0] !== patient.id) {
    t1 = insuranceQueryOptions.list(patient.id);
    $[0] = patient.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: t2
  } = useQuery(t1);
  let t3;
  if ($[2] !== patient.id || $[3] !== shouldUseTwoColumns || $[4] !== t2) {
    const coverages = t2 === undefined ? [] : t2;
    const sorted = [...coverages].sort(_temp);
    let t4;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Button variant="secondary" size="sm" icon={FiEdit}>Edit</Button>;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    let t5;
    if ($[7] !== patient.id) {
      t5 = <InsuranceEditDialog patientId={patient.id}>{t4}</InsuranceEditDialog>;
      $[7] = patient.id;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    t3 = <CollapsibleSection title="Insurance" persistKey="insurance" actions={t5}>{sorted.length === 0 ? <div className="py-6 text-center"><p className="text-body text-muted-foreground">No insurance on file</p></div> : <div className="space-y-6">{sorted.map(coverage => <CoverageBlock key={coverage.id} coverage={coverage} shouldUseTwoColumns={shouldUseTwoColumns} />)}</div>}</CollapsibleSection>;
    $[2] = patient.id;
    $[3] = shouldUseTwoColumns;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
function _temp(a, b) {
  return a.isPrimary === b.isPrimary ? 0 : a.isPrimary ? -1 : 1;
}
interface CoverageBlockProps {
  coverage: InsuranceCoverage;
  shouldUseTwoColumns: boolean;
}
function CoverageBlock(t0) {
  const $ = _c(63);
  const {
    coverage,
    shouldUseTwoColumns
  } = t0;
  const t1 = coverage.insurerName || "Coverage";
  let t2;
  if ($[0] !== t1) {
    t2 = <span className="text-body-emphasis text-foreground">{t1}</span>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = coverage.isPrimary ? "Primary" : "Secondary";
  const t4 = coverage.isPrimary ? "primary" : "muted";
  let t5;
  if ($[2] !== t3 || $[3] !== t4) {
    t5 = <CoverageBadge label={t3} tone={t4} />;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = coverage.inNetwork ? "In-network" : "Out-of-network";
  const t7 = coverage.inNetwork ? "success" : "muted";
  let t8;
  if ($[5] !== t6 || $[6] !== t7) {
    t8 = <CoverageBadge label={t6} tone={t7} />;
    $[5] = t6;
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== t2 || $[9] !== t5 || $[10] !== t8) {
    t9 = <div className="mb-2 flex items-center gap-2">{t2}{t5}{t8}</div>;
    $[8] = t2;
    $[9] = t5;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const t10 = shouldUseTwoColumns ? "grid-cols-2" : "grid-cols-1";
  let t11;
  if ($[12] !== t10) {
    t11 = cx("grid gap-x-6", t10);
    $[12] = t10;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== coverage.memberId) {
    t12 = <LabeledValue label="Member ID" value={coverage.memberId} />;
    $[14] = coverage.memberId;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const t13 = coverage.groupId ?? undefined;
  let t14;
  if ($[16] !== t13) {
    t14 = <LabeledValue label="Group ID" value={t13} />;
    $[16] = t13;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  const t15 = coverage.planName ?? undefined;
  let t16;
  if ($[18] !== t15) {
    t16 = <LabeledValue label="Plan Name" value={t15} />;
    $[18] = t15;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  const t17 = coverage.planType ?? undefined;
  let t18;
  if ($[20] !== t17) {
    t18 = <LabeledValue label="Plan Type" value={t17} />;
    $[20] = t17;
    $[21] = t18;
  } else {
    t18 = $[21];
  }
  const t19 = coverage.coverageType ?? undefined;
  let t20;
  if ($[22] !== t19) {
    t20 = <LabeledValue label="Coverage Type" value={t19} />;
    $[22] = t19;
    $[23] = t20;
  } else {
    t20 = $[23];
  }
  let t21;
  if ($[24] !== coverage.policyHolderName) {
    t21 = <LabeledValue label="Policy Holder" value={coverage.policyHolderName} />;
    $[24] = coverage.policyHolderName;
    $[25] = t21;
  } else {
    t21 = $[25];
  }
  let t22;
  if ($[26] !== coverage.policyHolderDob) {
    t22 = coverage.policyHolderDob ? formatDate(coverage.policyHolderDob) : undefined;
    $[26] = coverage.policyHolderDob;
    $[27] = t22;
  } else {
    t22 = $[27];
  }
  let t23;
  if ($[28] !== t22) {
    t23 = <LabeledValue label="Policy Holder DOB" value={t22} />;
    $[28] = t22;
    $[29] = t23;
  } else {
    t23 = $[29];
  }
  let t24;
  if ($[30] !== coverage.effectiveDate) {
    t24 = coverage.effectiveDate ? formatDate(coverage.effectiveDate) : undefined;
    $[30] = coverage.effectiveDate;
    $[31] = t24;
  } else {
    t24 = $[31];
  }
  let t25;
  if ($[32] !== t24) {
    t25 = <LabeledValue label="Effective" value={t24} />;
    $[32] = t24;
    $[33] = t25;
  } else {
    t25 = $[33];
  }
  let t26;
  if ($[34] !== coverage.effectiveTo) {
    t26 = coverage.effectiveTo ? formatDate(coverage.effectiveTo) : undefined;
    $[34] = coverage.effectiveTo;
    $[35] = t26;
  } else {
    t26 = $[35];
  }
  let t27;
  if ($[36] !== t26) {
    t27 = <LabeledValue label="Effective To" value={t26} />;
    $[36] = t26;
    $[37] = t27;
  } else {
    t27 = $[37];
  }
  let t28;
  if ($[38] !== coverage.phoneNumber) {
    t28 = coverage.phoneNumber ? formatPhoneNumber(coverage.phoneNumber) : undefined;
    $[38] = coverage.phoneNumber;
    $[39] = t28;
  } else {
    t28 = $[39];
  }
  let t29;
  if ($[40] !== t28) {
    t29 = <LabeledValue label="Phone" value={t28} />;
    $[40] = t28;
    $[41] = t29;
  } else {
    t29 = $[41];
  }
  const t30 = coverage.claimsAddress ?? undefined;
  let t31;
  if ($[42] !== t30) {
    t31 = <LabeledValue label="Claims Address" value={t30} />;
    $[42] = t30;
    $[43] = t31;
  } else {
    t31 = $[43];
  }
  const t32 = coverage.eligibilityNote ?? undefined;
  let t33;
  if ($[44] !== t32) {
    t33 = <LabeledValue label="Eligibility" value={t32} />;
    $[44] = t32;
    $[45] = t33;
  } else {
    t33 = $[45];
  }
  let t34;
  if ($[46] !== t11 || $[47] !== t12 || $[48] !== t14 || $[49] !== t16 || $[50] !== t18 || $[51] !== t20 || $[52] !== t21 || $[53] !== t23 || $[54] !== t25 || $[55] !== t27 || $[56] !== t29 || $[57] !== t31 || $[58] !== t33) {
    t34 = <div className={t11}>{t12}{t14}{t16}{t18}{t20}{t21}{t23}{t25}{t27}{t29}{t31}{t33}</div>;
    $[46] = t11;
    $[47] = t12;
    $[48] = t14;
    $[49] = t16;
    $[50] = t18;
    $[51] = t20;
    $[52] = t21;
    $[53] = t23;
    $[54] = t25;
    $[55] = t27;
    $[56] = t29;
    $[57] = t31;
    $[58] = t33;
    $[59] = t34;
  } else {
    t34 = $[59];
  }
  let t35;
  if ($[60] !== t34 || $[61] !== t9) {
    t35 = <div>{t9}{t34}</div>;
    $[60] = t34;
    $[61] = t9;
    $[62] = t35;
  } else {
    t35 = $[62];
  }
  return t35;
}
interface CoverageBadgeProps {
  label: string;
  tone: "primary" | "muted" | "success";
}
function CoverageBadge(t0) {
  const $ = _c(7);
  const {
    label,
    tone
  } = t0;
  const t1 = tone === "primary" && "bg-primary/10 text-primary";
  const t2 = tone === "success" && "bg-status-success-bg text-status-success-text";
  const t3 = tone === "muted" && "bg-background-secondary text-muted-foreground";
  let t4;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3) {
    t4 = cx("rounded-full px-2 py-0.5 text-caption", t1, t2, t3);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== label || $[5] !== t4) {
    t5 = <span className={t4}>{label}</span>;
    $[4] = label;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
}